import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import VerificationCodeModal from "../common/VerificationCodeModal";
import ResetPasswordModal from "../common/ResetPasswordModal";
import { validateEmail } from "../utils/passwordValidation";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [verifiedCode, setVerifiedCode] = useState("");
  const navigate = useNavigate();

  // Handle email change with validation
  const handleEmailChange = (e) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
    const error = validateEmail(newEmail);
    setEmailError(error);
  };

  // Step 1: Request password reset code
  const handleRequestCode = async (e) => {
    e.preventDefault();

    // Validate email
    const emailValidationError = validateEmail(email);
    setEmailError(emailValidationError);

    if (emailValidationError) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/auth/forgot-password/request`,
        { email }
      );

      toast.success(
        response.data.message || "Verification code sent to your email!"
      );
      setShowVerificationModal(true);
    } catch (error) {
      console.error("Error requesting reset code:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to send verification code. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // Step 2: Verify the code
  const handleVerifyCode = async (code) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/auth/forgot-password/verify`,
        { email, code }
      );

      toast.success("Code verified successfully!");
      setVerifiedCode(code);
      setShowVerificationModal(false);
      setShowResetPasswordModal(true);
    } catch (error) {
      console.error("Error verifying code:", error);
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Invalid or expired code"
      );
    }
  };

  // Step 3: Reset password
  const handleResetPassword = async (newPassword) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/auth/forgot-password/reset`,
        {
          email,
          code: verifiedCode,
          newPassword,
        }
      );

      toast.success("Password reset successfully!");
      setShowResetPasswordModal(false);

      // Redirect to login after a short delay
      setTimeout(() => {
        navigate("/login-arena", { replace: true });
      }, 1500);
    } catch (error) {
      console.error("Error resetting password:", error);
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to reset password. Please try again."
      );
      throw error;
    }
  };

  // Resend verification code
  const handleResendCode = async () => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/api/auth/forgot-password/request`,
        { email }
      );

      toast.success(response.data.message || "New verification code sent!");
    } catch (error) {
      console.error("Error resending code:", error);
      throw new Error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to resend code"
      );
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-[#f0f2f5] px-4 py-6">
      <div className="bg-white p-6 sm:p-10 rounded-2xl shadow-[0_10px_40px_rgba(0,0,0,0.1),0_1px_3px_rgba(0,0,0,0.05)] w-full max-w-[400px] backdrop-blur-[10px] border border-white/20">
        <img
          src="/logo.png"
          alt="Sports Hub Logo"
          className="block mx-auto mb-4 sm:mb-6 h-[50px] sm:h-[60px] drop-shadow-[0_2px_4px_rgba(0,0,0,0.1)]"
        />
        <h2 className="text-center text-[#2f3542] mb-2 sm:mb-3 font-bold text-xl sm:text-2xl tracking-[-0.5px]">
          Forgot Password
        </h2>
        <p className="text-center text-[#57606f] text-sm mb-6">
          Enter your email address and we'll send you a verification code to
          reset your password.
        </p>

        <form onSubmit={handleRequestCode} className="flex flex-col">
          <div className="mb-5">
            <label htmlFor="email" className="block mb-1.5 text-[#57606f]">
              Email Address
            </label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={handleEmailChange}
              placeholder="Enter your email"
              required
              className={`w-full h-11 px-3 rounded-lg text-base border transition-all duration-200 ${
                emailError
                  ? "border-[#dc3545] bg-[#fff5f5] shadow-[0_0_0_3px_rgba(220,53,69,0.1)]"
                  : "border-[#ddd] focus:border-[#5352ed] focus:shadow-[0_0_0_3px_rgba(83,82,237,0.1)]"
              } focus:outline-none`}
              disabled={isSubmitting}
            />
            {emailError && (
              <div className="text-[#dc3545] text-[13px] mt-1.5 mb-2.5 flex items-center gap-1">
                <span className="text-xs">⚠</span>
                {emailError}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isSubmitting || emailError !== ""}
            className="w-full h-11 bg-[#3498db] text-white border-none rounded-lg cursor-pointer transition-all duration-300 text-base font-semibold uppercase tracking-[0.5px] shadow-[0_4px_15px_rgba(52,152,219,0.3)] hover:scale-105 hover:shadow-[0_6px_20px_rgba(52,152,219,0.4)] active:scale-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {isSubmitting ? "Sending..." : "Send Verification Code"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/login-arena")}
            className="w-full h-11 mt-3 bg-white text-[#3498db] border border-[#3498db] rounded-lg cursor-pointer transition-all duration-300 text-base font-semibold hover:bg-[#f0f8ff] hover:scale-105 active:scale-100"
          >
            Back to Login
          </button>
        </form>
      </div>

      {/* Verification Code Modal */}
      <VerificationCodeModal
        isOpen={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        onVerify={handleVerifyCode}
        onResend={handleResendCode}
        email={email}
        timeLimit={60}
        title="Enter Verification Code"
        description="We've sent a 6-digit code to"
      />

      {/* Reset Password Modal */}
      <ResetPasswordModal
        isOpen={showResetPasswordModal}
        onClose={() => {
          setShowResetPasswordModal(false);
          setVerifiedCode("");
        }}
        onSubmit={handleResetPassword}
        title="Reset Password"
        description="Please enter your new password"
      />
    </div>
  );
};

export default ForgotPassword;
